/**
 * Whether Nastavení kanceláře offers its Bridge item: to `master.integrations.manage`, while the
 * server says the office may register bridges, or the office already has some (registering is
 * gated, managing is not).
 */
import { useQuery } from '@apollo/client/react';
import { MasterAccountPermission } from '@insio/shared-types';
import { useMasterPermissions } from '@/features/auth';
import { useMasterAccountBridgesEnabled } from '@/features/integrations/hooks/use-master-account-bridges-enabled';
import { GetBridgesDocument } from '@/graphql';

export interface OfficeMenuState {
  readonly visible: boolean;
  /** True while the answer may still change; `visible` is false until then. */
  readonly loading: boolean;
}

export function useOfficeBridgesMenuState(): OfficeMenuState {
  const { hasMasterPermission } = useMasterPermissions();
  const permitted = hasMasterPermission(MasterAccountPermission.INTEGRATIONS_MANAGE);
  const { enabled, loading: enabledLoading } = useMasterAccountBridgesEnabled({
    skip: !permitted
  });

  // Only the count is needed, and only while registering would not show the entry anyway.
  const { data, loading: countLoading } = useQuery(GetBridgesDocument, {
    variables: { first: 1 },
    skip: !permitted || enabled,
    fetchPolicy: 'cache-first'
  });

  const visible = permitted && (enabled || (data?.bridges.totalCount ?? 0) > 0);
  const loading = permitted && !visible && (enabledLoading || countLoading);
  return { visible, loading };
}

export function useOfficeBridgesMenu(): boolean {
  return useOfficeBridgesMenuState().visible;
}
